import type {
  FieldLike,
  FieldType,
  PrimaryObjectUploadStep,
  SmartConnector,
  SmartConnectorFlow,
  CSVFlowLoadDTO,
} from 'pages/SmartConnectors/types';
import {
  PRIMARY_OBJECT_UPLOAD_STEP_DATA_BY_STEP,
  PRIMARY_OBJECT_UPLOAD_STEPS,
  SMART_FIELD_TYPES,
} from 'pages/SmartConnectors/constants';
import { isCSVLoadStep } from '../../helpers';

export const getStepData = (
  data: SmartConnectorFlow,
  step: PrimaryObjectUploadStep
) => {
  const stepProps = PRIMARY_OBJECT_UPLOAD_STEP_DATA_BY_STEP[step];

  return stepProps.reduce<Partial<SmartConnectorFlow>>((acc, prop) => {
    return {
      ...acc,
      [prop]: data[prop],
    };
  }, {});
};

const propsByStep: Record<
  PrimaryObjectUploadStep,
  (keyof Omit<CSVFlowLoadDTO, 'id' | 'type' | 'custom_object' | 'order'>)[]
> = {
  [PRIMARY_OBJECT_UPLOAD_STEPS.matching]: ['matching_rules', 'scope'],
  [PRIMARY_OBJECT_UPLOAD_STEPS.mapping]: ['field_mapping_rules'],
  [PRIMARY_OBJECT_UPLOAD_STEPS.preview]: [
    'execution_variable',
    'newly_created_records_automations',
    'other_matches_records_automations',
    'automation_trigger_config',
  ],
};

export const getSmartConnectorFlowLoadsDTO = (
  data: Partial<SmartConnectorFlow>,
  loadId: string,
  step: PrimaryObjectUploadStep
) => {
  const flow: { loads?: Partial<CSVFlowLoadDTO>[] } = {};

  if (data.loads) {
    flow.loads = data.loads.map((load, i) => {
      if (isCSVLoadStep(load)) {
        const loadPayload: Partial<CSVFlowLoadDTO> = {
          id: load.id,
          custom_object: load.custom_object,
          order: i,
        };
        if (loadId === load.id || (loadId === 'new' && !load.id)) {
          propsByStep[step].forEach((prop) => {
            switch (prop) {
              case 'execution_variable': {
                loadPayload[prop] = load[prop];
                break;
              }
              case 'field_mapping_rules': {
                loadPayload[prop] = (load.field_mapping_rules || []).map(
                  (rule, j) => ({
                    ...rule,
                    field:
                      !rule.field || rule.field.startsWith('new_')
                        ? undefined
                        : rule.field,
                    variables: rule.variables?.length
                      ? rule.variables
                      : undefined,
                    display_order: j,
                  })
                );
                break;
              }
              case 'matching_rules': {
                loadPayload[prop] = (load.matching_rules || []).map(
                  (rule, j) => ({
                    ...rule,
                    order: j,
                  })
                );
                break;
              }
              case 'newly_created_records_automations': {
                loadPayload[prop] = (
                  load.newly_created_records_automations || []
                ).map(({ id }) => id);
                break;
              }
              case 'other_matches_records_automations': {
                loadPayload[prop] = (
                  load.other_matches_records_automations || []
                ).map(({ id }) => id);
                break;
              }
              case 'automation_trigger_config': {
                loadPayload[prop] = load[prop];
                break;
              }
              default:
                loadPayload[prop] = load[prop];
            }
          });
        }

        // We don't want to touch other loads, so we pass only id, custom_object and order
        // for rest of the values values to not to be touched and validated on BE
        return loadPayload;
      } else {
        return {
          id: load.id,
          order: i,
        };
      }
    });
  }

  return { flow };
};

export const canFieldBeMatchedToArray = (field: FieldLike) => {
  if (
    field.fieldType === SMART_FIELD_TYPES.relationship &&
    ['one_to_many', 'many_to_many'].includes(field.relation?.cardinality)
  ) {
    return true;
  }
  if (
    (
      [
        SMART_FIELD_TYPES.dynamictags,
        SMART_FIELD_TYPES.checkboxes,
      ] as FieldType[]
    ).includes(field.fieldType)
  ) {
    return true;
  }

  return false;
};

export const getValidFlowLoads = (
  data: SmartConnector,
  loadId: string,
  fallBackScope: string
) => {
  const loadIndex = (data.flow.loads || []).findIndex(
    (load) => (loadId === 'new' && !load.id) || load.id === loadId
  );
  const scopes = (data.flow.loads || []).reduce<Record<string, true>>(
    (acc, load) => {
      if (!isCSVLoadStep(load)) {
        load.return_scopes?.forEach((returnScope) => {
          if (returnScope.name) {
            acc[returnScope.name] = true;
          }
        });
      }
      return acc;
    },
    Object.keys(data.headers || []).reduce<Record<string, true>>(
      (acc, scope) => ({ ...acc, [scope]: true }),
      {}
    )
  );

  const validVariables = (data.execution_variables || []).reduce(
    (acc, variable) => {
      if (scopes[variable.scope]) {
        acc[variable.id] = true;
      }
      return acc;
    },
    {} as Record<string, true>
  );

  data.flow.loads?.forEach((load) => {
    if (isCSVLoadStep(load)) {
      if (load.execution_variable && scopes[load.scope]) {
        validVariables[load.execution_variable.id] = true;
      }
    } else {
      load.return_scopes?.forEach((returnScope) => {
        if (
          returnScope.execution_variables?.length &&
          scopes[returnScope.name]
        ) {
          returnScope.execution_variables.forEach((variable) => {
            validVariables[variable.id] = true;
          });
        }
      });
    }
  });

  data.flow.additional_variables?.forEach(({ id }) => {
    validVariables[id] = true;
  });

  return [
    ...(data.flow.loads || []).map((load, i: number) =>
      i === loadIndex && isCSVLoadStep(load)
        ? {
            ...load,
            matching_rules: (load.matching_rules || []).map(
              ({ variable, ...rest }) => ({
                ...rest,
                variable:
                  variable && validVariables[variable] ? variable : null,
              })
            ),
            field_mapping_rules: (load.field_mapping_rules || []).map(
              ({ variables, ...rest }) => ({
                ...rest,
                variables: (variables || []).filter(
                  (variable) => validVariables[variable]
                ),
              })
            ),
            scope: scopes[load.scope] ? load.scope : fallBackScope,
          }
        : load
    ),
  ];
};
